/**
 * @module GoogleMapsLoader
 * @description Dynamically loads the Google Maps JavaScript API script.
 */

/**
 * Loads the Google Maps JavaScript API by injecting a script tag into the document head.
 * - If the key is falsy, logs a warning and resolves immediately.
 * - If `window.google` already exists, resolves immediately (idempotency guard).
 * - On success, logs an info message and resolves.
 * - On failure, logs an error and **resolves** (never rejects) so the boot sequence continues.
 *
 * @param {string|null} googleApiKey - The Google Maps API key.
 * @returns {Promise<void>} A Promise that always resolves.
 */
export function loadGoogleMaps(googleApiKey) {
  if (!googleApiKey) {
    console.warn("Google Maps key is null — skipping initialization");
    return Promise.resolve();
  }

  if (window.google) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places&language=en`;
    script.async = true;

    script.onload = () => {
      console.info("Google Maps script loaded dynamically");
      resolve();
    };

    script.onerror = () => {
      console.error("Google Maps script failed to load");
      resolve();
    };

    document.head.appendChild(script);
  });
}
